// ====================================================================
// WORDVILLE — the word-games zone, down the east road
// ====================================================================
// The first themed zone, and the test drive for the world architecture:
// its terrain is a plateau mask in the heightfield, its scatter props join
// the chunk system (so they stream like everything else), and its buildings
// reuse the exact builders the village uses — passed in via ctx because zone
// CONTENT belongs out here, not in main.js.
//
// What's in it: three game huts wired to word minigames through the same
// slug machinery as the village (all three show the standard Coming Soon
// card until their embeds ship — add a slug to PLAYABLE_GAME_SLUGS in
// main.js to switch one live), a library, a giant dictionary that teaches a
// Word of the Day, and a small herd of Word Oxen that wander the meadow and
// share a grade-appropriate word when bumped. Rooftop signs are passed as
// explicit placements (editable like all signs via the ! editor).
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW } from '../../core/tier.js';
import { PALETTE, ACCENTS, BUILDING_SCHEMES } from '../../core/palette.js';
import { addObstacle } from '../collision.js';
import { heightAt } from '../heightfield.js';
import { bakeColor, coloredBox } from '../geoColor.js';
import { makeCanvasTexture } from '../../core/texture.js';
import { spawnActor } from '../actors.js';
import { markDynamic } from '../freezeStatics.js';

export const WORDVILLE_CENTER = { x: 44, z: 0 };

// One kid-sized word per calendar day, same for the whole class.
const WORDS_OF_THE_DAY = [
  ['WIGGLE',      'to move with little twisty shakes — like a happy puppy'],
  ['GIGANTIC',    'really, REALLY big. Bigger than big!'],
  ['WHISPER',     'to talk in your quietest, softest voice'],
  ['MARVELOUS',   'so wonderful it makes you say WOW'],
  ['ZIGZAG',      'a line that goes this way, then THAT way, then this way…'],
  ['COZY',        'warm, snug and comfy — like a blanket fort'],
  ['CURIOUS',     'full of questions and ready to find answers'],
  ['SPARKLE',     'to shine with lots of tiny flashes of light'],
  ['GRUMBLE',     'a low growly sound a tummy (or a grump) makes'],
  ['FLUFFY',      'soft and puffy, like a cloud or a kitten'],
  ['SPLENDID',    'super great! Amazing! Fantastic!'],
  ['SNOOZE',      'a short little nap'],
  ['PECULIAR',    'strange in an interesting way'],
  ['SHIMMER',     'to glow with a soft, dancing light'],
  ['DAYDREAM',    'a wide-awake dream about lovely things'],
  ['SCRUMPTIOUS', 'so delicious you want another bite right away'],
];

// The oxen's pocket dictionaries, by grade band. The ?grade= URL param (the
// same one game launches pass through) picks the band; no param mixes both.
const OX_WORDS_K2 = [
  ['HAPPY',  'feeling good and smiley inside'],
  ['JUMP',   'to push off the ground with both feet — boing!'],
  ['TINY',   'very very small, like a ladybug'],
  ['LOUD',   'a big noise! Cover your ears!'],
  ['BRAVE',  'doing something even when it feels scary'],
  ['SILLY',  'funny in a goofy way'],
  ['SHINY',  'so bright and clean it glows'],
  ['QUIET',  'making almost no sound at all… shhh'],
  ['GIGGLE', 'a little laugh that sneaks out of you'],
  ['HUGE',   'super duper big'],
  ['SLEEPY', 'ready for a nap and a yawn'],
  ['WARM',   'cozy like sunshine on your back'],
];
const OX_WORDS_35 = [
  ['ENORMOUS', 'extremely large — bigger than huge'],
  ['FRAGILE',  'easily broken; handle it gently'],
  ['ANCIENT',  'very, very old — from long, long ago'],
  ['GLIMPSE',  'a quick little look at something'],
  ['VIVID',    'so bright and clear it almost glows'],
  ['SOAR',     'to fly high and smooth, like an eagle'],
  ['BRISK',    'quick and full of energy, like a cold morning walk'],
  ['MURMUR',   'a soft, low, blurry sort of talking'],
  ['DAZZLE',   'to amaze someone with brightness or brilliance'],
  ['TIMID',    'shy and a little bit nervous'],
  ['CLEVER',   'quick at figuring things out'],
  ['VAST',     'stretching out enormously far — like the ocean'],
];

function wordOfTheDay() {
  const now = new Date();
  const idx = (now.getMonth() * 31 + now.getDate()) % WORDS_OF_THE_DAY.length;
  return WORDS_OF_THE_DAY[idx];
}

// Resolved once and memoized: the grade flag can't change mid-session, so the
// bump handler (updateWordville) must not re-parse the URL + concat every time.
let _oxWordList = null;
function oxWordList() {
  if (_oxWordList) return _oxWordList;
  const grade = (new URLSearchParams(location.search).get('grade') || '').toLowerCase();
  if (['k', 'k1', '1', '2'].includes(grade)) _oxWordList = OX_WORDS_K2;
  else if (['3', '4', '5'].includes(grade)) _oxWordList = OX_WORDS_35;
  else _oxWordList = OX_WORDS_K2.concat(OX_WORDS_35);
  return _oxWordList;
}

// A book cover: deep red with a double gold border and gold title.
function makeBookCoverTexture(title, subtitle) {
  return makeCanvasTexture(384, 512, (ctx, c) => {
  ctx.fillStyle = '#7b3436';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.strokeStyle = '#d8a93c';
  ctx.lineWidth = 10;
  ctx.strokeRect(18, 18, c.width - 36, c.height - 36);
  ctx.lineWidth = 3;
  ctx.strokeRect(34, 34, c.width - 68, c.height - 68);
  ctx.fillStyle = '#e8c96a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '900 58px Georgia, "Times New Roman", serif';
  ctx.fillText(title, c.width / 2, c.height * 0.40);
  ctx.font = '700 30px Georgia, "Times New Roman", serif';
  ctx.fillText(subtitle, c.width / 2, c.height * 0.62);
  }, { anisotropy: LOW ? 1 : 4 });
}

// A toy letter block face: bright background, big friendly letter.
function makeLetterTexture(letter, bg, ink) {
  return makeCanvasTexture(128, 128, (ctx) => {
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 128, 128);
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 7;
  ctx.strokeRect(8, 8, 112, 112);
  ctx.fillStyle = ink;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '900 84px ui-sans-serif, system-ui, sans-serif';
  ctx.fillText(letter, 64, 70);
  });
}

function buildLetterBlock(ctx, letter, bg, x, z, yaw, size = 0.42) {
  const gy = heightAt(x, z);
  const block = new THREE.Mesh(
    new THREE.BoxGeometry(size, size, size),
    new THREE.MeshLambertMaterial({ map: makeLetterTexture(letter, bg, '#ffffff') })
  );
  block.position.set(x, gy + size / 2, z);
  block.rotation.y = yaw;
  block.castShadow = !LOW;
  ctx.propsGroup.add(block);
}

// Bunting: a sagging string of bright pennants between two posts. One merged
// BufferGeometry + one line mesh = two draw calls for the whole string.
function buildBunting(ctx, x1, z1, x2, z2, y = 1.45) {
  const FLAGS = 9;
  const colors = ACCENTS;
  const positions = [];
  const cols = [];
  const c = new THREE.Color();
  const sag = 0.35;
  const pointAt = (t) => ({
    x: x1 + (x2 - x1) * t,
    z: z1 + (z2 - z1) * t,
    y: y - Math.sin(Math.PI * t) * sag,
  });
  for (let i = 0; i < FLAGS; i++) {
    const t0 = (i + 0.15) / FLAGS, t1 = (i + 0.85) / FLAGS;
    const a = pointAt(t0), b = pointAt(t1);
    const tip = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2, y: (a.y + b.y) / 2 - 0.28 };
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z, tip.x, tip.y, tip.z);
    c.set(colors[i % colors.length]);
    for (let v = 0; v < 3; v++) cols.push(c.r, c.g, c.b);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
  const flags = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: true })
  );
  ctx.propsGroup.add(flags);
  // The string itself: a thin stretched box post-to-post.
  const len = Math.hypot(x2 - x1, z2 - z1);
  const line = new THREE.Mesh(
    new THREE.BoxGeometry(len, 0.015, 0.015),
    new THREE.MeshBasicMaterial({ color: PALETTE.cream, fog: true })
  );
  line.position.set((x1 + x2) / 2, y - sag * 0.55, (z1 + z2) / 2);
  line.rotation.y = -Math.atan2(z2 - z1, x2 - x1);
  ctx.propsGroup.add(line);
}

// The giant dictionary: a monument-sized standing book with a Word of the Day.
function buildGiantDictionary(ctx, x, z) {
  const group = new THREE.Group();
  const mat = (color) => new THREE.MeshLambertMaterial({ color });

  // Pages block between two covers, spine on the left.
  const pages = new THREE.Mesh(new THREE.BoxGeometry(1.6, 2.1, 0.42), mat(PALETTE.cream));
  pages.position.y = 1.12;
  group.add(pages);
  for (const side of [-1, 1]) {
    const cover = new THREE.Mesh(new THREE.BoxGeometry(1.78, 2.26, 0.07), mat(PALETTE.wordRed));
    cover.position.set(0.04, 1.12, side * 0.245);
    cover.castShadow = !LOW;
    group.add(cover);
  }
  const spine = new THREE.Mesh(new THREE.BoxGeometry(0.09, 2.26, 0.56), mat(PALETTE.wordRedDark));
  spine.position.set(-0.86, 1.12, 0);
  group.add(spine);
  // Title plane just proud of the front cover (which faces the plaza).
  const title = new THREE.Mesh(
    new THREE.PlaneGeometry(1.55, 2.0),
    new THREE.MeshBasicMaterial({ map: makeBookCoverTexture('DICTIONARY', '— ABC → XYZ —'), transparent: false })
  );
  title.position.set(0.04, 1.12, -0.29);
  title.rotation.y = Math.PI;
  group.add(title);

  const gy = heightAt(x, z);
  group.position.set(x, gy, z);
  group.rotation.z = 0.05;            // a storybook lean
  ctx.propsGroup.add(group);

  addObstacle({ minX: x - 1.0, maxX: x + 1.0, minY: gy, maxY: gy + 2.3, minZ: z - 0.4, maxZ: z + 0.4 });

  // Bump the book and it teaches today's word.
  const [word, def] = wordOfTheDay();
  ctx.addBumpZone(x, z - 0.6, 1.9, 'dictionary', (sourceId) => {
    ctx.showBumpMsg(`📖 Word of the Day: ${word}`, def, 7, sourceId);
  });
}

// ---------- WORD OXEN ----------
// Gentle box-built oxen that amble around the Wordville meadow, each with a
// letter block strapped to its back. Bump one and it shares a word. They are
// actors (world/actors.js) and follow the terrain like everything else.
//
// Chromebook discipline: all the colored parts are pre-merged into TWO shared
// vertex-colored geometries (static body, dipping head) used by every ox, so
// a whole ox is 3 draw calls (body, head, letter pack) instead of 16 meshes
// with 10 materials — the same trick as the village's instanced props,
// shaped for an articulated critter.
const oxen = [];
let _zoneCtx = null;
let _oxShared = null;

function oxShared() {
  if (_oxShared) return _oxShared;
  // Static parts, in body space.
  const bodyGeo = mergeGeometries([
    coloredBox(1.15, 0.62, 0.6,   0,     0.62, 0,    0x8a6a4a),  // body
    coloredBox(0.16, 0.36, 0.16,  0.42,  0.18, 0.2,  0x6e5238),  // legs ×4
    coloredBox(0.16, 0.36, 0.16,  0.42,  0.18, -0.2, 0x6e5238),
    coloredBox(0.16, 0.36, 0.16, -0.42,  0.18, 0.2,  0x6e5238),
    coloredBox(0.16, 0.36, 0.16, -0.42,  0.18, -0.2, 0x6e5238),
    coloredBox(0.3, 0.07, 0.07,  -0.68,  0.72, 0,    0x6e5238),  // tail
    coloredBox(0.34, 0.66, 0.64, -0.1,   0.62, 0,    0x4a3526),  // pack strap
  ], false);
  // Head parts, LOCAL to the head pivot at body-space (0.72, 0.74, 0) — the
  // whole face (muzzle, horns, ears) dips together while grazing.
  const headGeo = mergeGeometries([
    coloredBox(0.42, 0.4, 0.42,  0,     0,     0,     0x7c5e40),  // head
    coloredBox(0.18, 0.2, 0.3,   0.24, -0.08,  0,     0xc4a884),  // muzzle
    coloredBox(0.09, 0.09, 0.3,  0,     0.21,  0.3,   0xe8e0d0, -0.5),   // horns
    coloredBox(0.09, 0.09, 0.3,  0,     0.21, -0.3,   0xe8e0d0,  0.5),
    coloredBox(0.06, 0.12, 0.14, -0.1,  0.12,  0.26,  0x7c5e40),  // ears
    coloredBox(0.06, 0.12, 0.14, -0.1,  0.12, -0.26,  0x7c5e40),
  ], false);
  _oxShared = {
    bodyGeo, headGeo,
    packGeo: new THREE.BoxGeometry(0.36, 0.36, 0.36),
    mat: new THREE.MeshLambertMaterial({ vertexColors: true }),
  };
  return _oxShared;
}

function buildWordOx(ctx, x, z, letter, blockColor) {
  const shared = oxShared();
  const group = new THREE.Group();

  const body = new THREE.Mesh(shared.bodyGeo, shared.mat);
  body.castShadow = !LOW;
  group.add(body);

  const head = new THREE.Group();
  head.position.set(0.72, 0.74, 0);
  head.add(new THREE.Mesh(shared.headGeo, shared.mat));
  group.add(head);

  const pack = new THREE.Mesh(
    shared.packGeo,
    new THREE.MeshLambertMaterial({ map: makeLetterTexture(letter, blockColor, '#ffffff') })
  );
  pack.position.set(-0.1, 1.12, 0);
  pack.rotation.y = 0.2;
  group.add(pack);

  group.position.set(x, heightAt(x, z), z);
  markDynamic(group);   // updateWordville wanders/bobs the whole ox
  ctx.propsGroup.add(group);

  const ox = {
    group, head,
    x, z,
    facing: Math.random() * Math.PI * 2,
    state: 'graze',
    stateT: 1 + Math.random() * 2,
    tx: x, tz: z,
    speed: 0.55 + Math.random() * 0.25,
    bumpCooldown: 0,
    bobPhase: Math.random() * Math.PI * 2,
  };
  ox.actor = spawnActor('wordox', ox);
  oxen.push(ox);
}

function oxPickTarget(ox) {
  // Wander the meadow ring around the plaza. New headings stay within ~70° of
  // the current bearing so the straight walk between ring points never cuts a
  // chord through the buildings (oxen don't collide with props — by design,
  // they're scenery-gentle — so their paths must keep them honest instead).
  const cur = Math.atan2(ox.z - WORDVILLE_CENTER.z, ox.x - WORDVILLE_CENTER.x);
  const a = cur + (Math.random() - 0.5) * 2.4;
  const r = 9.5 + Math.random() * 3.5;
  ox.tx = WORDVILLE_CENTER.x + Math.cos(a) * r;
  ox.tz = WORDVILLE_CENTER.z + Math.sin(a) * r * 0.8;
}

// Called from the main loop while in play mode.
export function updateWordville(dt, t, playerPos) {
  if (!_zoneCtx) return;
  // The herd sleeps when nobody's near the zone.
  const zoneDist = Math.hypot(playerPos.x - WORDVILLE_CENTER.x, playerPos.z - WORDVILLE_CENTER.z);
  if (zoneDist > 45) return;

  for (const ox of oxen) {
    ox.bumpCooldown = Math.max(0, ox.bumpCooldown - dt);
    ox.stateT -= dt;

    const pdx = playerPos.x - ox.x, pdz = playerPos.z - ox.z;
    const pDist = Math.hypot(pdx, pdz);

    // Bumped! Share a word, then stand and look at the kid for a moment.
    if (pDist < 1.35 && ox.bumpCooldown <= 0) {
      const list = oxWordList();
      const [word, def] = list[(Math.random() * list.length) | 0];
      _zoneCtx.showBumpMsg(`🐂 Word Ox says: ${word}`, def, 6, 'wordox');
      ox.bumpCooldown = 9;
      ox.state = 'graze';
      ox.stateT = 3.5;
      ox.facing = Math.atan2(pdx, pdz);
    }

    if (ox.state === 'graze') {
      if (ox.stateT <= 0) { ox.state = 'amble'; oxPickTarget(ox); }
    } else {
      const dx = ox.tx - ox.x, dz = ox.tz - ox.z;
      const d = Math.hypot(dx, dz);
      if (d < 0.4 || ox.stateT < -20) {
        ox.state = 'graze';
        ox.stateT = 2 + Math.random() * 3;
      } else {
        const want = Math.atan2(dx, dz);
        let diff = want - ox.facing;
        while (diff > Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;
        ox.facing += diff * Math.min(1, dt * 2.5);
        ox.x += Math.sin(ox.facing) * ox.speed * dt;
        ox.z += Math.cos(ox.facing) * ox.speed * dt;
      }
    }

    // Pose: terrain-following, soft walk bob, head dips while grazing.
    const walking = ox.state === 'amble';
    const bob = walking ? Math.sin(t * 7 + ox.bobPhase) * 0.035 : 0;
    ox.group.position.set(ox.x, heightAt(ox.x, ox.z) + bob, ox.z);
    ox.group.rotation.y = ox.facing - Math.PI / 2;   // body's long axis is X
    const headDip = ox.state === 'graze' ? 0.55 : Math.sin(t * 7 + ox.bobPhase) * 0.06;
    ox.head.position.y = 0.74 - headDip * 0.25;
    ox.head.rotation.z = -headDip * 0.6;
  }
}

export function buildWordville(ctx) {
  _zoneCtx = ctx;
  const { x: CX } = WORDVILLE_CENTER;

  // ---- The road in: continue the east road from where its stones ended.
  ctx.buildPath([[27, 0], [31, 0], [35, 0], [38.5, 0]]);
  // Plaza spine + spurs to each doorway.
  ctx.buildPath([[38.5, 0], [41.5, 0], [44.5, 0], [48, 0]]);
  ctx.buildPath([[41, 0], [40.5, -2.4]]);          // Last Word
  ctx.buildPath([[47, 0], [47.5, -2.4]]);          // Word Hunt
  ctx.buildPath([[47, 0.4], [47.5, 2.6]]);         // Rhyme Time
  ctx.buildPath([[41.5, 0.4], [40.8, 2.6]]);       // Library
  ctx.buildPath([[44, 0.4], [44, 5.6]]);           // the Dictionary

  // Daisies along the walk in — little colour beats on the journey.
  const wordFlowers = [0xe05656, 0x4a8ed0, 0xe0a832, 0x7c5a9c];
  ctx.buildFlowerPatch(30.2, 1.7, 5, 0.55, wordFlowers);
  ctx.buildFlowerPatch(33.6, -1.8, 5, 0.55, wordFlowers);

  // ---- Welcome sign at the zone entrance (yaw reads from the play camera),
  // and the signpost home.
  ctx.addHutSign(38.2, 1.7, 'WORDVILLE', { x: 38.2, y: 0, z: 1.7, yaw: 0.60 });
  ctx.buildSignpost(37.6, -1.5, [{ text: 'CHOCKYTOWN', yaw: 0.6, flip: true }]);
  ctx.addBumpZone(38.2, 1.7, 1.3, 'wordville-sign', (sourceId) => {
    ctx.showBumpMsg('WELCOME TO WORDVILLE!', 'Where words come out to play 🔤', 4, sourceId);
  });

  // ---- Game huts (doors face +z, same as the village). Signs sit on the
  // rooftops, angled toward the diorama camera; nudge them anytime with the
  // ! sign editor.
  //                     x     z    w    d    wallH  wall      roof      smoke  name          slug         rooftop sign
  ctx.buildGameHut(40.5, -4.2, 2.4, 2.2, 1.5, BUILDING_SCHEMES.wordLavender.wall, BUILDING_SCHEMES.wordLavender.roof, true,  'LAST WORD',  'last-word',  { x: 40.5, y: 1.30, z: -4.2, yaw: 0.60 });
  ctx.buildGameHut(47.5, -4.2, 2.2, 2.4, 1.4, BUILDING_SCHEMES.wordGreen.wall, BUILDING_SCHEMES.wordGreen.roof, false, 'WORD HUNT',  'word-hunt',  { x: 47.5, y: 1.20, z: -4.2, yaw: 0.60 });
  ctx.buildGameHut(47.5,  4.2, 2.4, 2.0, 1.6, BUILDING_SCHEMES.wordTerracotta.wall, BUILDING_SCHEMES.wordTerracotta.roof, true,  'RHYME TIME', 'rhyme-time', { x: 47.5, y: 1.40, z:  4.2, yaw: 0.60 });

  // ---- The library: the big building of the zone. Handmade kit model when
  // it loaded; the procedural big house otherwise.
  if (!ctx.buildKitStructure || !ctx.buildKitStructure('house_big', 40.8, 4.8, { name: 'Library' })) {
    ctx.buildHouse(40.8, 4.8, 4.6, 3.2, 2.5, 0xead9b8, 0x3e5a7c, false);
  }
  ctx.addHutSign(40.8, 4.8, 'LIBRARY', { x: 40.8, y: 2.35, z: 4.8, yaw: 0.60 });
  ctx.addBumpZone(40.8, 2.6, 1.6, 'library', (sourceId) => {
    ctx.showBumpMsg('THE LIBRARY 📚', 'Shhh… story time is coming soon!', 4, sourceId);
  });
  // A little stack of returned books by the door.
  ctx.addBox(42.6, 0.09, 3.1, 0.55, 0.18, 0.4, 0x9c4a3c, false);
  ctx.addBox(42.55, 0.26, 3.05, 0.5, 0.16, 0.36, 0x3e6a8c, false);
  ctx.addBox(42.62, 0.41, 3.12, 0.44, 0.14, 0.33, 0x6a8a4a, false);

  // ---- The giant dictionary, looming kindly over the plaza's north side.
  buildGiantDictionary(ctx, CX, 7);
  // Toy letter blocks tumbled at its feet.
  buildLetterBlock(ctx, 'A', '#e05656', 42.9, 6.1, 0.4);
  buildLetterBlock(ctx, 'B', '#4a8ed0', 45.2, 6.3, -0.3);
  buildLetterBlock(ctx, 'C', '#e0a832', 45.0, 5.7, 0.9, 0.34);

  // ---- Dressing: bunting over the plaza, lanterns, flowers, trees.
  ctx.buildLantern(42, -1.6, false);
  ctx.buildLantern(46, 1.6, false);
  buildBunting(ctx, 42, -1.6, 46, 1.6);
  ctx.buildFlowerPatch(42.6, 1.6, 7, 0.8, wordFlowers);
  ctx.buildFlowerPatch(45.6, -1.7, 7, 0.8, wordFlowers);
  ctx.buildFlowerPatch(38.9, -1.8, 5, 0.6, wordFlowers);
  ctx.buildFlowerPatch(49.3, 0.6, 6, 0.7, wordFlowers);
  ctx.buildTree(37.8, -5.2, 1.1);
  ctx.buildTree(50.8, -6.0, 1.2);
  ctx.buildTree(51.5, 3.2, 0.95);
  ctx.buildTree(44.6, 9.6, 1.3);
  ctx.buildTree(37.5, 7.2, 0.9);
  ctx.buildBush(39.2, 5.9, 0.9);
  ctx.buildBush(49.8, 1.4, 0.8);
  ctx.buildRock(50.4, -1.9, 0.8);
  ctx.buildMushroomCluster(38.4, 6.6, 3);
  ctx.buildMushroomCluster(50.4, -5.2, 3);
  ctx.buildMushroomCluster(45.4, 9.0, 2);

  // ---- The herd. Letters spell it out for anyone paying attention.
  const herd = LOW ? [['W', '#e05656', 39, -8], ['O', '#4a8ed0', 50, 5.5]]
                   : [['W', '#e05656', 39, -8], ['O', '#4a8ed0', 50, 5.5], ['X', '#7c5a9c', 47, 9]];
  for (const [letter, color, x, z] of herd) buildWordOx(ctx, x, z, letter, color);
}
